import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import UserService from '@/services/UserService';
import { AGENT_SUPERVISION_ENFORCEMENT_FLAG } from '@/components/Company/permissions/constants';

// An administrator may lift a lock before it expires, so a known lock is
// trusted only for a while before letting the backend decide again.
const RECHECK_MS = 5 * 60 * 1000;
// Forced fetches closer than this to the last one add nothing, e.g. several
// consumers reacting to the same window focus.
const FORCE_GAP_MS = 5 * 1000;

const initialState = {
  locks: {},
  fetchedUntil: 0,
};

const lockUntil = (expiresAt) =>
  Math.min(
    Date.now() + RECHECK_MS,
    expiresAt ? Date.parse(expiresAt) : Infinity
  );

// `force` skips the recheck window, for whenever the backend may have changed
// the locks.
export const fetchSupervisionLocks = createAsyncThunk(
  'AgentSupervision/fetchSupervisionLocks',
  () => UserService.getSupervisionLocks(),
  {
    condition: (options, { getState }) => {
      const { AgentSupervisionSlice, AuthSlice } = getState();
      const { fetchedUntil } = AgentSupervisionSlice;
      return (
        (options?.force
          ? Date.now() - (fetchedUntil - RECHECK_MS) > FORCE_GAP_MS
          : fetchedUntil < Date.now()) &&
        Boolean(
          AuthSlice?.permissions?.waffle_flags?.includes(
            AGENT_SUPERVISION_ENFORCEMENT_FLAG
          )
        )
      );
    },
  }
);

export const AgentSupervisionSlice = createSlice({
  name: 'AgentSupervision',
  initialState,
  reducers: {
    setSupervisionLock: {
      reducer(state, action) {
        const { action: lockedAction, message, until } = action.payload;
        state.locks[lockedAction] = { message, until, setAt: Date.now() };
      },
      prepare(detail) {
        return {
          payload: {
            action: detail.action,
            message: detail.message,
            until: lockUntil(detail.expires_at),
          },
        };
      },
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSupervisionLocks.pending, (state) => {
        state.fetchedUntil = Date.now() + RECHECK_MS;
      })
      .addCase(fetchSupervisionLocks.rejected, (state) => {
        state.fetchedUntil = 0;
      })
      .addCase(fetchSupervisionLocks.fulfilled, (state, action) => {
        // The backend knows every active lock, so a lifted one is dropped,
        // unless a rejected action locked it after this request started.
        const startedAt = state.fetchedUntil - RECHECK_MS;
        const newer = Object.entries(state.locks).filter(
          ([, lock]) => lock.setAt >= startedAt
        );
        state.locks = Object.fromEntries([
          ...Object.entries(action.payload.locks).map(
            ([lockedAction, lock]) => [
              lockedAction,
              { message: lock.message, until: lockUntil(lock.expires_at) },
            ]
          ),
          ...newer,
        ]);
      });
  },
});

export const selectSupervisionLock = (action) => (state) => {
  const lock = state.AgentSupervisionSlice.locks[action];
  return lock && lock.until > Date.now() ? lock : null;
};

export const { setSupervisionLock } = AgentSupervisionSlice.actions;

export default AgentSupervisionSlice.reducer;
